import { useState, useRef, useEffect, useCallback } from 'react';
import { useNotifications, isAbortError } from '@medprompt/shared';
import { hasPatientCodesData } from '../detail/PatientDiaryPanel';

const useConsultationPatient = ({ consultationId, consultation, consultationService }) => {
  const { addNotification } = useNotifications();

  const [patientData, setPatientData] = useState(null);
  const [patientDataError, setPatientDataError] = useState(false);
  const [patientRecordData, setPatientRecordData] = useState(null);
  const [patientRecordDataError, setPatientRecordDataError] = useState(false);
  const [refreshingPatientCodes, setRefreshingPatientCodes] = useState(false);
  const refreshingPatientCodesRef = useRef(false);
  // Monotonic token to discard consultation-scoped responses that arrive after the user has
  // navigated to a different consultation or the patient was rematched.
  const consultationScopedFetchIdRef = useRef(0);

  const consultationPatientId = consultation?.patientId;
  const consultationConsultationId = consultation?.consultationId;

  const consultationPatientIdRef = useRef(null);
  useEffect(() => {
    consultationPatientIdRef.current = consultationPatientId || null;
  }, [consultationPatientId]);

  // requestId is captured against consultationScopedFetchIdRef so a response for a consultation
  // the user has already navigated away from cannot overwrite current state.
  const fetchPatientData = useCallback(async (targetConsultationId, requestId, options) => {
    try {
      setPatientDataError(false);
      const patient = await consultationService.fetchPatientData(targetConsultationId, options);
      if (requestId !== consultationScopedFetchIdRef.current) return;
      setPatientData(patient);
    } catch (error) {
      if (isAbortError(error) || requestId !== consultationScopedFetchIdRef.current) return;
      console.error('Failed to fetch patient data:', error);
      setPatientData(null);
      setPatientDataError(true);
    }
  }, [consultationService]);

  const fetchPatientRecordData = useCallback(async (targetConsultationId, requestId, options) => {
    try {
      setPatientRecordDataError(false);
      const patientRecord = await consultationService.fetchPatientRecordData(
        targetConsultationId, options);
      if (requestId !== consultationScopedFetchIdRef.current) return;
      setPatientRecordData(patientRecord);
    } catch (error) {
      if (isAbortError(error) || requestId !== consultationScopedFetchIdRef.current) return;
      console.error('Failed to fetch patient record data:', error);
      setPatientRecordData(null);
      setPatientRecordDataError(true);
    }
  }, [consultationService]);

  useEffect(() => {
    if (!consultationPatientId || !consultationConsultationId) {
      consultationScopedFetchIdRef.current++;
      setPatientData(null);
      setPatientDataError(false);
      setPatientRecordData(null);
      setPatientRecordDataError(false);
      setRefreshingPatientCodes(false);
      refreshingPatientCodesRef.current = false;
      return undefined;
    }

    const requestId = ++consultationScopedFetchIdRef.current;
    setRefreshingPatientCodes(false);
    refreshingPatientCodesRef.current = false;
    const controller = new AbortController();
    const options = { signal: controller.signal };

    fetchPatientData(consultationConsultationId, requestId, options);
    fetchPatientRecordData(consultationConsultationId, requestId, options);

    // Moving to another consultation, or leaving, should free the downstream reads rather than
    // leave them running for a screen nobody is looking at.
    return () => controller.abort();
  }, [consultationPatientId, consultationConsultationId, fetchPatientData, fetchPatientRecordData]);

  const handleRefreshPatientCodes = useCallback(async () => {
    if (!consultationId || refreshingPatientCodesRef.current) return;
    const requestId = consultationScopedFetchIdRef.current;
    refreshingPatientCodesRef.current = true;
    setRefreshingPatientCodes(true);
    try {
      const codes = await consultationService.fetchFreshPatientCodes(consultationId);
      if (requestId !== consultationScopedFetchIdRef.current) return;
      if (!hasPatientCodesData(codes)) {
        addNotification(`patient-codes-refresh-empty-${consultationId}`, 'Could not refresh problems from EMIS', { isError: true });
        return;
      }
      setPatientRecordData(previous => (
        previous ? { ...previous, patientCodesData: codes } : previous
      ));
    } catch (error) {
      if (requestId !== consultationScopedFetchIdRef.current) return;
      console.error('Failed to refresh patient codes:', error);
      addNotification(`patient-codes-refresh-error-${consultationId}`, 'Failed to refresh problems from EMIS', { isError: true });
    } finally {
      refreshingPatientCodesRef.current = false;
      if (requestId === consultationScopedFetchIdRef.current) {
        setRefreshingPatientCodes(false);
      }
    }
  }, [consultationId, consultationService, addNotification]);

  /** Reconnect catch-up: refetch the patient for the current scope if the consultation has one. */
  const refetchPatient = useCallback((options) => {
    if (!consultationPatientIdRef.current) return;
    const requestId = consultationScopedFetchIdRef.current;
    fetchPatientData(consultationId, requestId, options);
    fetchPatientRecordData(consultationId, requestId, options);
  }, [consultationId, fetchPatientData, fetchPatientRecordData]);

  // The diary badge and panel read patientRecordData.pendingReferrals, so completing or deleting
  // has to drop the referral locally rather than wait for a patient-record refresh.
  const removePendingReferralLocally = useCallback((referralId) => {
    setPatientRecordData(previous => (
      previous?.pendingReferrals
        ? {
            ...previous,
            pendingReferrals: previous.pendingReferrals.filter(
              referral => referral.referralId !== referralId
            )
          }
        : previous
    ));
  }, []);

  const applyPendingReferralPatch = useCallback((referralId, fields) => {
    setPatientRecordData(previous => (
      previous?.pendingReferrals
        ? {
            ...previous,
            pendingReferrals: previous.pendingReferrals.map(referral => (
              referral.referralId === referralId ? { ...referral, ...fields } : referral
            ))
          }
        : previous
    ));
  }, []);

  return {
    patientData,
    patientDataError,
    patientRecordData,
    patientRecordDataError,
    pendingReferralsCount: patientRecordData?.pendingReferrals?.length || 0,
    patientName: patientData?.name || patientRecordData?.patient?.name || null,
    refreshingPatientCodes,
    handleRefreshPatientCodes,
    refetchPatient,
    removePendingReferralLocally,
    applyPendingReferralPatch,
  };
};

export default useConsultationPatient;
